<template>
  <section class="halyk">
    <img :src="bannerImage" class="halyk__bg-img" alt="halyk" />

    <h3 class="halyk__title">{{ t('halyk.title') }}</h3>

    <div class="halyk__action">
      <img :src="actionImage" alt="action" />
      <div class="halyk__info">
        <span class="halyk__info-more">{{ t('halyk.infoMore') }}:</span>
        <span>{{ isParent ? t('halyk.infoParent') : t('halyk.info') }}</span>
      </div>
    </div>

    <div class="halyk__drawing">
      <span>
        {{ t('halyk.drawing_1') }}<span class="halyk__drawing-more">{{ t('halyk.drawingMore') }}</span>
        {{ t('halyk.drawing_2') }}
      </span>
      <div class="mt-2">
        {{ t('halyk.drawing_3') }}
        <a :href="promoUrl" target="_blank" rel="noopener noreferrer" @click.prevent="openExternal(promoUrl)">{{ promoUrl }}</a>
      </div>
      <div v-if="isParent" class="mt-2">
        <a :href="videoUrl" target="_blank" rel="noopener noreferrer" @click.prevent="openExternal(videoUrl)">{{ t('halyk.halykVideo') }}</a>
      </div>
    </div>

    <template v-if="isParent">
      <label class="halyk__checkbox">
        <input type="checkbox" :checked="checked" @change="checked = !checked" />
        <span>{{ t('halyk.checkboxInfo') }}</span>
      </label>

      <button
        type="button"
        class="halyk__next-btn"
        :class="{ 'halyk__next-btn--active': checked }"
        :disabled="!checked || halykMutation.isPending.value"
        @click="openHalykCabinet"
      >
        {{ t('continue_button_label') }}
      </button>
    </template>

    <div v-else class="halyk__child-info">
      {{ t('halyk.infoChild') }}
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import actionImage from '@/assets/feed/halyk/action.png';
import halykLarge from '@/assets/feed/halyk/halykBg.png';
import halykMedium from '@/assets/feed/halyk/halykBgMiddle.jpg';
import halykSmall from '@/assets/feed/halyk/halykBgSmall.jpg';
import { useHalykUserInfoMutation } from '@/queries';
import { useTopbarStore } from '@/stores/topbar';
import { useViewerStore } from '@/stores/viewer';

const { t } = useI18n();
const router = useRouter();
const viewerStore = useViewerStore();
const topbarStore = useTopbarStore();
const halykMutation = useHalykUserInfoMutation();

const checked = ref(false);
const width = ref(typeof window !== 'undefined' ? window.innerWidth : 1280);

const isParent = computed(() => viewerStore.role === 'parent');
const promoUrl = computed(() => String(import.meta.env.VITE_HALYK_PROMO_URL || ''));
const videoUrl = computed(() => {
  if (localStorage.getItem('translation') === 'kz') {
    return import.meta.env.VITE_HALYK_VIDEO_KZ_URL || '';
  }
  return import.meta.env.VITE_HALYK_VIDEO_RU_URL || '';
});

const bannerImage = computed(() => {
  // Legacy parity: in old app check for <=768 was first.
  if (width.value <= 768) return halykMedium;
  if (width.value <= 575) return halykSmall;
  return halykLarge;
});

function onResize() {
  width.value = window.innerWidth;
}

function openExternal(url) {
  const target = window?.cordova ? '_system' : '_blank';
  window.open(url, target);
}

async function openHalykCabinet() {
  const response = await halykMutation.mutateAsync();
  if (response?.url) {
    openExternal(response.url);
  }
}

onMounted(() => {
  topbarStore.setTitle(t('back_button_label'));
  topbarStore.addButton({
    type: 'left',
    icon: 'keyboard_backspace',
    action: () => router.push('/feed')
  });

  if (typeof window !== 'undefined') {
    window.addEventListener('resize', onResize);
  }
});

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', onResize);
  }
});
</script>

<style scoped>
.halyk {
  margin-top: 46px;
  padding-left: 24px;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.halyk__bg-img {
  width: 100%;
}

.halyk__title {
  font-weight: 500;
  font-size: 32px;
  line-height: 40px;
}

.halyk__action {
  border: 1px solid #bdddff;
  background: #e4f2ff;
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  border-radius: 4px;
  padding: 16px 12px;
  align-items: flex-start;
}

.halyk__info {
  font-size: 18px;
  line-height: 32px;
  color: #333;
}

.halyk__info-more {
  color: #3b82f6;
  font-weight: 700;
}

.halyk__drawing {
  font-size: 18px;
  line-height: 32px;
}

.halyk__drawing-more {
  font-weight: 700;
}

.halyk__checkbox {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 16px;
  line-height: 24px;
  color: #4f4f4f;
}

.halyk__checkbox input {
  margin-top: 6px;
}

.halyk__next-btn {
  margin-top: 32px;
  width: 280px;
  height: 40px;
  border: none;
  border-radius: 2px;
  background: #dedede;
  color: #333;
  box-shadow: 0 1px 1px 0 #0000003d;
  text-transform: uppercase;
  font-weight: 500;
  font-size: 13px;
}

.halyk__next-btn:disabled {
  opacity: 0.8;
}

.halyk__next-btn--active {
  background: #36b453;
  color: #fff;
}

.halyk__child-info {
  font-size: 18px;
  line-height: 32px;
  color: #333;
  border: 1px solid #dfe4ea;
  background: #f2f2f2;
  border-radius: 4px;
  padding: 16px;
}

@media (max-width: 768px) {
  .halyk {
    padding: 32px;
    margin-top: 0;
  }

  .halyk__title {
    font-size: 24px;
    line-height: 32px;
  }

  .halyk__action {
    padding: 12px;
  }

  .halyk__info {
    line-height: 28px;
  }

  .halyk__drawing {
    line-height: 28px;
  }

  .halyk__child-info {
    font-size: 16px;
    line-height: 28px;
    padding: 12px;
  }

  .halyk__checkbox {
    font-size: 14px;
    line-height: 20px;
  }
}

@media (max-width: 520px) {
  .halyk {
    padding: 16px 20px;
    gap: 16px;
  }

  .halyk__title {
    font-size: 18px;
    line-height: 24px;
  }

  .halyk__info,
  .halyk__drawing {
    font-size: 16px;
    line-height: 24px;
  }

  .halyk__action {
    padding: 8px;
  }

  .halyk__child-info {
    font-size: 14px;
    line-height: 20px;
    padding: 8px;
  }

  .halyk__checkbox {
    font-size: 12px;
    line-height: 16px;
  }

  .halyk__next-btn {
    width: 100%;
  }
}
</style>
